/**
 * web/src/pages/UserAccess.tsx
 *
 * Admin-only User Access page. Surfaces the user roster with per-row
 * editing of role + Slack User ID, a per-user EVENT-ACCESS editor, and a
 * delete affordance. Event grants ARE enforced server-side (pixel.access):
 * a non-admin user sees + acts on only the events granted here; admin
 * bypasses scoping. Coarse — a grant covers the whole event.
 *
 * Auth: client-side gate matches EventManagement — server-side
 * requireRole('admin') on every /api/admin/* route is the security
 * boundary. Non-admin users see an "Admin only" Empty fallback.
 */
import { useMemo, useState } from 'react';
import { useAccessMatrix } from '../hooks/queries/useAccessMatrix';
import { useSetTopbar } from '../components/layout/Topbar';
import { RoleGuard } from '../components/layout/RoleGuard';
import { AccessToolbar } from '../components/access/AccessToolbar';
import { UserList } from '../components/access/UserList';
import { Empty } from '../components/shared/Empty';
import { Skeleton } from '../components/shared/Skeleton';
import { Icon } from '../lib/icons';

/**
 * UserAccess page. Admin-gated client-side; every backing /api/admin/users
 * route is server-enforced admin-only. Renders the toolbar + roster table.
 *
 * @sideEffects React Query subscription on ['admin-access']
 * @idempotent yes — multiple mounts share the cache entry
 */
export function UserAccess() {
  const { data, isPending, error } = useAccessMatrix();
  const [query, setQuery] = useState('');

  useSetTopbar({ title: 'User Access' });

  const filteredUsers = useMemo(() => {
    if (!data) return null;
    if (!query) return data.users;
    const q = query.toLowerCase();
    return data.users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [data, query]);

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton variant="row" height={36} />
        <Skeleton variant="card" height={300} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Empty
        title="Couldn't load user access"
        description={error instanceof Error ? error.message : ''}
      />
    );
  }

  return (
    <RoleGuard
      roles={['admin']}
      fallback={
        <Empty
          icon={<Icon name="user" size={20} />}
          title="Admin only"
          description="User Access is restricted to admin users. Contact the admin team if you need workflow access changed."
        />
      }
    >
      <div className="max-w-[920px]">
        <AccessToolbar query={query} onQueryChange={setQuery} />
        <UserList
          users={filteredUsers ?? data.users}
          events={data.events}
          eventGrants={data.eventGrants}
        />
      </div>
    </RoleGuard>
  );
}
